import { rootSelectors } from '@redux/modules/selectors';
import store from '../services/store';
import { extractDate, validDate } from '../components/Section/History/dateranges';

export const dateLimits = (relationship, birthdate) => {
  let max = new Date();
  let min = extractDate(birthdate);
  const today = new Date();

  switch (relationship) {
    case 'Self':
      max = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate());
      min = new Date(today.getFullYear() - 100, today.getMonth(), today.getDate());
      break;
    case 'Mother':
    case 'Father':
      max = min;
      min = new Date(today.getFullYear() - 200, today.getMonth(), today.getDate());
      break;
    case 'Child':
      break;
    case 'Stepmother':
    case 'Stepfather':
    case 'Fosterparent':
    case 'Stepchild':
    case 'Brother':
    case 'Sister':
    case 'Stepbrother':
    case 'Stepsister':
    case 'Half-brother':
    case 'Half-sister':
    case 'Father-in-law':
    case 'Mother-in-law':
    case 'Guardian':
    case 'Other':
    case 'None':
    // Everything else just uses the defaults
    // 'None' is a special case passed from Relatives component DOB date control indicating that
    // no relationship was selected to address EN-4811
      min = new Date(today.getFullYear() - 200, today.getMonth(), today.getDate());
      break;
    default:
    // handles null, undefined, empty string
    // these are for the rest of the DateControls that aren't from Relatives component so the relationship
    // will be undefined
      min = min || new Date(today.getFullYear() - 200, today.getMonth(), today.getDate());
  }
  return { minDate: min, maxDate: max };
};

// * We should not be using this in a render function of a component, as it will
//   provide unreliable results.
// * This is calling store directly, which makes testing a bit more difficult.
//   With most react-redux, you use a provider to provide a mockState. As this
//   isn't pulling from the provider, you can't pull from that mocked state. Any
//   tests relying on this may need to mock this function out, see
//   implementation with
//   /components/Section/Identification/Identification.test.tsx
const getContext = () => ({
  applicantBirthdate: rootSelectors.application.getApplicantBirthdateAsDate(store.getState()),
});

export default class DateControlValidator {
  constructor(data = {}, context = null) {
    this.month = data.month;
    this.day = data.day;
    this.year = data.year;
    this.hideMonth = data.hideMonth;
    this.hideDay = data.hideDay;
    this.maxDate = extractDate(data.maxDate);
    this.maxDateEqualTo = data.maxDateEqualTo || false;
    this.minDate = extractDate(data.minDate);
    this.minDateEqualTo = data.minDateEqualTo || false;
    this.noMaxDate = data.noMaxDate;
    this.relationship = data.relationship;
    this.forceMinDate = data.forceMinDate;
    context = context || getContext(); // eslint-disable-line no-param-reassign

    this.limits = dateLimits(this.relationship, context.applicantBirthdate);
    if (!this.maxDate || (this.maxDate && this.maxDate > this.limits.maxDate)) {
      this.maxDate = this.limits.maxDate;
    }

    if (
      !this.minDate
      || (this.minDate && this.minDate < this.limits.minDate && !this.forceMinDate)
    ) {
      this.minDate = this.limits.minDate;
    }

    // For month/year fields, we compare from the start of the month
    if (this.hideMonth || this.hideDay) {
      this.maxDate = new Date(
        `${this.hideMonth ? '1' : this.maxDate.getMonth() + 1}/${
          this.hideDay ? '1' : this.maxDate.getDate()
        }/${this.maxDate.getFullYear()}`
      );
      this.minDate = new Date(
        `${this.hideMonth ? '1' : this.minDate.getMonth() + 1}/${
          this.hideDay ? '1' : this.minDate.getDate()
        }/${this.minDate.getFullYear()}`
      );
    }
  }

  validMaxDate() {
    // If no max day present, we return true to continue as we have nothing
    // to compare against
    if (!this.maxDate || this.noMaxDate) {
      return true;
    }

    if (!validDate(this)) {
      return false;
    }

    const date = extractDate(this);

    if (this.maxDateEqualTo) {
      return date <= this.maxDate;
    }

    return date <= this.maxDate;
  }

  validMinDate() {
    // If no min day present, we return true to continue as we have nothing
    // to compare against
    if (!this.minDate) {
      return true;
    }

    if (!validDate(this)) {
      return false;
    }

    const date = extractDate(this);
    if (this.minDateEqualTo) {
      return date >= this.minDate;
    }

    return date > this.minDate;
  }

  validDate() {
    if ((!this.day && !this.hideDay) || (!this.month && !this.hideMonth) || !this.year) {
      return false;
    }
    return true;
  }

  isValid() {
    return this.validDate() && this.validMaxDate() && this.validMinDate();
  }
}
